import type { FilePlugin, UploadOptions } from '../../types'
import type { BlobObject } from '../../types/gallery'
import type { ImageMeta } from '../../types/image-meta'

const DEFAULT_UPLOAD_CONCURRENCY = 3

type UploadApiResponse = {
  count: number
  files: BlobObject[]
}

type UploadSlotsApiResponse = {
  galleryEntryOrder: string[]
}

export default defineNuxtPlugin(() => {
  const images = ref<BlobObject[]>([])
  const router = useRouter()
  const toast = useToast()

  function createUploadPlaceholder(file: File): BlobObject {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

    return {
      pathname: `__upload__/${uploadId}`,
      key: `upload:${file.name}`,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
      uploadedAt: new Date().toISOString(),
      uploadId,
      uploadFileName: file.name,
      uploadProgress: 0,
      uploadStatus: 'queued',
      isUploading: true
    }
  }

  function updateUploadPlaceholder(uploadId: string, updater: (image: BlobObject) => BlobObject) {
    images.value = images.value.map((image) => {
      if (image.uploadId !== uploadId) {
        return image
      }

      return updater(image)
    })
  }

  function replaceUploadPlaceholder(uploadId: string, uploadedImage: BlobObject) {
    images.value = images.value.map((image) => {
      if (image.uploadId !== uploadId) {
        return image
      }

      return uploadedImage
    })
  }

  function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const e = error as { data?: { message?: string, statusMessage?: string }, message?: string }
      return e.data?.message || e.data?.statusMessage || e.message || 'Upload failed'
    }

    return 'Upload failed'
  }

  function uploadSingleImageWithProgress(file: File, galleryName: string, uploadId: string): Promise<BlobObject> {
    return new Promise((resolve, reject) => {
      if (typeof XMLHttpRequest === 'undefined') {
        reject(new Error('Upload progress is only available in the browser'))
        return
      }

      const xhr = new XMLHttpRequest()
      const formData = new FormData()
      formData.append('gallery', galleryName)
      formData.append('uploadId', uploadId)
      formData.append('file', file)

      xhr.open('POST', '/api/images/upload', true)
      xhr.responseType = 'json'

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) {
          return
        }

        const progress = Math.min(99, Math.round((event.loaded / event.total) * 100))

        updateUploadPlaceholder(uploadId, image => ({
          ...image,
          uploadStatus: 'uploading',
          uploadProgress: progress,
          isUploading: true
        }))
      }

      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          const responseError = typeof xhr.response === 'object' && xhr.response
            ? (xhr.response as { message?: string, statusMessage?: string })
            : null

          reject(new Error(responseError?.message || responseError?.statusMessage || `Upload failed with status ${xhr.status}`))
          return
        }

        const payload = xhr.response as UploadApiResponse | null
        const uploaded = payload?.files?.[0]

        if (!uploaded) {
          reject(new Error('Upload succeeded but no file metadata was returned.'))
          return
        }

        resolve(uploaded)
      }

      xhr.onerror = () => reject(new Error('Network error during upload'))
      xhr.onabort = () => reject(new Error('Upload aborted'))

      xhr.send(formData)
    })
  }

  async function runWithConcurrency<T>(items: T[], limit: number, worker: (item: T, index: number) => Promise<void>) {
    let cursor = 0
    const size = Math.max(1, Math.min(limit, items.length || 1))

    const workers = Array.from({ length: size }, async () => {
      while (cursor < items.length) {
        const index = cursor++
        const item = items[index]

        if (item === undefined) {
          continue
        }

        await worker(item, index)
      }
    })

    await Promise.all(workers)
  }

  async function getImages(galleryName: string) {
    const headers = import.meta.server ? useRequestHeaders(['cookie']) : undefined

    const file = await $fetch<BlobObject[]>('/api/images', {
      headers,
      query: { gallery: galleryName }
    })

    images.value = file
  }

  async function uploadImages(files: File[], galleryName: string, redirectTo?: string, options?: UploadOptions) {
    if (!files.length) {
      return
    }

    const placeholders = files.map(file => createUploadPlaceholder(file))

    images.value = [...placeholders, ...images.value]

    // Reserving gallery order slots up front keeps selection order regardless of which upload finishes first.
    const reservation = await $fetch<UploadSlotsApiResponse>('/api/images/upload-slots', {
      method: 'POST',
      body: {
        gallery: galleryName,
        uploadIds: placeholders.map(placeholder => placeholder.uploadId)
      }
    }).catch(() => null)

    if (reservation) {
      options?.onUploadSlotsReserved?.(reservation.galleryEntryOrder)
    }

    let successfulUploads = 0
    let failedUploads = 0

    const concurrency = options?.concurrency || DEFAULT_UPLOAD_CONCURRENCY

    await runWithConcurrency(files, concurrency, async (file, index) => {
      const placeholder = placeholders[index]
      if (!placeholder?.uploadId) {
        return
      }

      updateUploadPlaceholder(placeholder.uploadId, image => ({
        ...image,
        uploadStatus: 'uploading',
        uploadProgress: 0,
        isUploading: true,
        uploadError: undefined
      }))

      try {
        const uploaded = await uploadSingleImageWithProgress(file, galleryName, placeholder.uploadId)

        replaceUploadPlaceholder(placeholder.uploadId, uploaded)
        options?.onImageUploaded?.(placeholder.uploadId, uploaded)
        successfulUploads += 1
      }
      catch (error) {
        const message = extractErrorMessage(error)

        updateUploadPlaceholder(placeholder.uploadId, image => ({
          ...image,
          uploadStatus: 'error',
          uploadProgress: image.uploadProgress || 0,
          isUploading: true,
          uploadError: message
        }))

        failedUploads += 1
      }
    })

    if (failedUploads > 0) {
      toast.add({
        color: 'red',
        title: `Failed to upload ${failedUploads} image${failedUploads > 1 ? 's' : ''}`,
        description: `${successfulUploads} uploaded successfully.`
      })
    }

    if (redirectTo) {
      await router.push(redirectTo)
    }
  }

  async function uploadImage(image: File, galleryName: string, redirectTo?: string, options?: UploadOptions) {
    await uploadImages([image], galleryName, redirectTo, options)
  }

  async function deleteImage(pathname: string, galleryName: string) {
    await $fetch(`/api/images/${pathname}`, { method: 'DELETE' })

    await getImages(galleryName)
  }

  async function updateImageMeta(pathname: string, meta: Partial<Pick<ImageMeta, 'title' | 'description' | 'cameraModel' | 'aperture' | 'focalLength' | 'exposureTime'>>) {
    const updated = await $fetch<{ meta: ImageMeta }>(`/api/images/meta/${pathname}`, {
      method: 'PATCH',
      body: meta
    })

    images.value = images.value.map((image) => {
      if (image.pathname !== pathname) {
        return image
      }

      return {
        ...image,
        meta: updated.meta
      }
    })

    return updated.meta
  }

  return {
    provide: {
      file: {
        getImages,
        images,
        uploadImage,
        uploadImages,
        deleteImage,
        updateImageMeta
      } as FilePlugin
    }
  }
})
